import React, { useState, useEffect, memo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { usePushNotifications } from '../hooks/usePushNotifications';
import {
  LayoutDashboard, Building2, LogOut, Shield, Users,
  Sun, Moon, Activity, Menu, X, DollarSign, Search, LifeBuoy, Bell, BellOff, Package, AlertTriangle, Plug, BarChart3, FileText,
} from 'lucide-react';

const NAV = [
  { to: '/',        icon: LayoutDashboard, label: 'Dashboard',      end: true },
  { to: '/orgs',    icon: Building2,       label: 'Organizations'             },
  { to: '/users',   icon: Search,          label: 'User Search'               },
  { to: '/plans',   icon: Package,         label: 'Plans'                     },
  { to: '/billing', icon: DollarSign,      label: 'Billing & MRR'             },
  { to: '/support', icon: LifeBuoy,        label: 'Support'                   },
  { to: '/admins',  icon: Users,           label: 'System Admins'             },
  { to: '/audit',   icon: Activity,        label: 'Audit Log'                 },
  { to: '/data-audit', icon: FileText,     label: 'Data Audit Trail'          },
  { to: '/integrations',   icon: Plug,       label: 'API Integrations'        },
  { to: '/api-usage',      icon: BarChart3,  label: 'API Usage'               },
  { to: '/errors',  icon: AlertTriangle,   label: 'System Errors'             },
  { to: '/security', icon: Shield,         label: 'Security'                  },
  { to: '/latency',  icon: Activity,        label: 'Latency Monitor'           },
];

// ── PushButton isolated so its async state never re-renders Layout ────────────
// usePushNotifications fires getPushStatus() on mount → setSubscribed → state change.
// If that hook lives in Layout, every state update re-renders ALL children including
// the active page, blowing away input focus. Isolating it here breaks that chain.
const PushButton = memo(function PushButton() {
  const push = usePushNotifications();
  if (!push.supported) return null;
  return (
    <button
      className="theme-toggle"
      onClick={push.subscribed ? push.unsubscribe : push.subscribe}
      disabled={push.loading}
      title={
        push.permission === 'denied'
          ? 'Notifications blocked — check browser settings'
          : push.subscribed
            ? 'Push notifications ON — click to disable'
            : 'Enable push notifications'
      }
      style={{ position: 'relative' }}
    >
      {push.loading ? (
        <span className="spinner" style={{ width: 13, height: 13 }} />
      ) : push.subscribed ? (
        <Bell size={15} style={{ color: 'var(--gold)' }} />
      ) : (
        <BellOff size={15} style={{ opacity: push.permission === 'denied' ? 0.4 : 0.7 }} />
      )}
      {push.subscribed && (
        <span style={{
          position: 'absolute', top: 2, right: 2,
          width: 6, height: 6, borderRadius: '50%',
          background: 'var(--green)',
          boxShadow: '0 0 4px rgba(34,197,94,0.6)',
        }} />
      )}
    </button>
  );
});

// ── Layout ────────────────────────────────────────────────────────────────────
export default function Layout({ children, title, crumb }) {
  const { user, logout }  = useAuth();
  const navigate          = useNavigate();
  const location          = useLocation();
  const { theme, toggle } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  const initials = user
    ? `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() || '?'
    : '?';

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="admin-shell">

      {/* ── Mobile overlay ── */}
      <div
        className={`sidebar-overlay${sidebarOpen ? ' open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ── Sidebar ── */}
      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-brand" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div className="wordmark">Zeus<span>CMS</span></div>
            <div className="sub">System Admin</div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
            className="sidebar-close"
          >
            <X size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section-label">Navigation</div>
          {NAV.map(({ to, icon: Icon, label, end, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
            >
              <Icon size={15} />
              {label}
              {badge ? (
                <span style={{
                  marginLeft: 'auto', minWidth: 18, height: 18,
                  background: 'var(--amber)', color: '#fff',
                  borderRadius: 9, fontSize: '0.6rem', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 5px',
                }}>{badge}</span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.first_name} {user?.last_name}</div>
              <div className="sidebar-user-role">
                <Shield size={9} style={{ marginRight: 3, verticalAlign: '-1px' }} />
                System Admin
              </div>
            </div>
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={handleLogout}
            style={{ marginTop: 12, width: '100%', justifyContent: 'center', color: 'var(--text-muted)' }}
          >
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="main">
        <div className="topbar">
          <button className="hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <Menu size={17} />
          </button>

          <span className="topbar-title">{title}</span>
          {crumb && <span className="topbar-crumb">{crumb}</span>}

          <div style={{ flex: 1 }} />

          {/* PushButton is isolated — its state updates never re-render Layout or children */}
          <PushButton />

          <button
            className="theme-toggle"
            onClick={toggle}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>
        </div>

        <div className="page-content">
          {children}
        </div>
      </div>
    </div>
  );
}
